import React from "react";
import {
  Box,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import { getCadence } from "lib/cadence";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// Fee configuration (matches backend)
const FEE_CONFIG = {
  platformFeePercent: 1.5, // 1.5% platform fee
  platformFeeMax: 5.0, // Platform fee capped at $5.00
  card: {
    percentFee: 2.9, // Stripe card fee: 2.9%
    fixedFee: 0.3, // Stripe card fixed fee: $0.30
  },
  bank: {
    percentFee: 0.8, // Stripe ACSS/ACH fee: 0.8%
    fixedFee: 0,
    maxFee: 5.0, // Capped at $5.00
  },
};

// Calculate fees for a given amount and payment method type
const calculateFees = (baseAmount: number, type: "card" | "bank") => {
  const config = FEE_CONFIG[type];

  // Calculate Stripe fee
  let stripeFee = (baseAmount * config.percentFee) / 100 + config.fixedFee;

  // Apply cap if exists (for bank transfers)
  if ("maxFee" in config && stripeFee > config.maxFee) {
    stripeFee = config.maxFee;
  }

  // Calculate platform fee (capped at $5)
  let platformFee = (baseAmount * FEE_CONFIG.platformFeePercent) / 100;
  if (platformFee > FEE_CONFIG.platformFeeMax) {
    platformFee = FEE_CONFIG.platformFeeMax;
  }

  // Total fees and final amount
  const totalFees = stripeFee + platformFee;
  const totalAmount = baseAmount + totalFees;

  return {
    stripeFee: parseFloat(stripeFee.toFixed(2)),
    platformFee: parseFloat(platformFee.toFixed(2)),
    totalFees: parseFloat(totalFees.toFixed(2)),
    totalAmount: parseFloat(totalAmount.toFixed(2)),
  };
};

interface PaymentBreakdownProps {
  monthlyContribution: number;
  currency?: string;
  intervalUnit?: "day" | "week" | "month";
  intervalCount?: number;
}

export const PaymentBreakdown: React.FC<PaymentBreakdownProps> = ({
  monthlyContribution,
  currency = "CAD",
  intervalUnit,
  intervalCount,
}) => {
  const theme = useTheme();

  const cardFees = calculateFees(monthlyContribution, "card");
  const bankFees = calculateFees(monthlyContribution, "bank");
  const savings = cardFees.totalFees - bankFees.totalFees;

  return (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        Payment Breakdown
      </Typography>

      {/* Contribution Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {getCadence(intervalUnit, intervalCount).adjective} Contribution
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          ${monthlyContribution.toFixed(2)} {currency}
        </Typography>
      </Box>

      {/* Processing Fee Row with Tooltip */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Processing Fees
          </Typography>
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Fee breakdown:
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  • Stripe payment processing
                </Typography>
                <Typography variant="caption" component="div">
                  • Platform fee (1.5%)
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: 1, fontStyle: "italic", opacity: 0.9 }}
                >
                  Bank transfers have lower fees than cards!
                </Typography>
              </Box>
            }
            arrow
            placement="top"
          >
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary" }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" fontWeight={500}>
          ${bankFees.totalFees.toFixed(2)} – ${cardFees.totalFees.toFixed(2)}
        </Typography>
      </Box>

      {/* Divider */}
      <Divider sx={{ my: 1.5 }} />

      {/* Payment Method Options */}
      <Box sx={{ mb: 2 }}>
        {/* Bank Option */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon
              sx={{ fontSize: 18, color: "success.main" }}
            />
            <Typography variant="body2" color="success.main" fontWeight={500}>
              Bank Transfer
            </Typography>
          </Box>
          <Typography variant="body2" color="success.main" fontWeight={600}>
            ${bankFees.totalAmount.toFixed(2)}
          </Typography>
        </Box>

        {/* Card Option */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.grey[500], 0.08),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreditCardIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Credit/Debit Card
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            ${cardFees.totalAmount.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Savings Message */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 1,
          borderRadius: 1,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "success.main", fontWeight: 600 }}
        >
          Save ${savings.toFixed(2)} {currency} with bank transfer!
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentBreakdown;
